/**
 * Sentry Zaraz Consent Integration
 *
 * A wrapper around @imviidx/sentry-consent-integration specifically designed for
 * Cloudflare Zaraz consent management. This package provides a simplified API
 * for integrating Sentry with Zaraz consent choices.
 */
import { sentryConsentIntegration, } from '@imviidx/sentry-consent-integration';
/**
 * Creates a Sentry integration that works with Cloudflare Zaraz consent management.
 *
 * This is a convenience wrapper around @imviidx/sentry-consent-integration that
 * automatically handles Zaraz consent API integration.
 *
 * @param options Configuration options for Zaraz consent integration
 * @returns Sentry integration for use in integrations array
 *
 * @example
 * ```typescript
 * import { sentryZarazConsentIntegration } from 'sentry-zaraz-consent-integration';
 * import * as Sentry from '@sentry/browser';
 *
 * Sentry.init({
 *   dsn: 'your-sentry-dsn',
 *   integrations: [
 *     sentryZarazConsentIntegration({
 *       purposeMapping: {
 *         functional: ['essential'],     // Requires 'essential' purpose to be granted
 *         analytics: ['analytics'],      // Requires 'analytics' purpose to be granted
 *         marketing: ['marketing'],      // Requires 'marketing' purpose to be granted
 *         preferences: ['preferences'],  // Requires 'preferences' purpose to be granted
 *       },
 *       debug: true,
 *     }),
 *   ],
 * });
 * ```
 */
export function sentryZarazConsentIntegration(options) {
    const { purposeMapping, zarazTimeout = 30000, debug = false } = options;
    // Create consent state getters that check Zaraz API
    const consentStateGetters = {
        functional: () => checkZarazConsent(purposeMapping.functional),
        analytics: () => checkZarazConsent(purposeMapping.analytics),
        marketing: () => checkZarazConsent(purposeMapping.marketing),
        preferences: () => checkZarazConsent(purposeMapping.preferences),
    };
    // Setup consent change listener for Zaraz events
    const onConsentChange = (trigger) => {
        // Listen for Zaraz consent events
        const handleConsentChange = () => {
            if (debug) {
                console.log('[SentryZarazConsentIntegration] Zaraz consent changed');
            }
            trigger();
        };
        // Listen for various Zaraz consent events
        document.addEventListener('zarazConsentChoicesUpdated', handleConsentChange);
        document.addEventListener('zarazConsentAPIReady', handleConsentChange);
        // Return cleanup function
        return () => {
            document.removeEventListener('zarazConsentChoicesUpdated', handleConsentChange);
            document.removeEventListener('zarazConsentAPIReady', handleConsentChange);
        };
    };
    // Create the base consent integration options
    const integrationOptions = {
        consentStateGetters,
        onConsentChange,
        consentTimeout: zarazTimeout,
        debug,
    };
    if (debug) {
        console.log('[SentryZarazConsentIntegration] Initializing with options:', {
            purposeMapping,
            zarazTimeout,
            debug,
        });
    }
    // Return the base integration with our Zaraz-specific configuration
    return sentryConsentIntegration(integrationOptions);
}
/**
 * Helper function to check consent for a specific purpose mapping
 */
function checkZarazConsent(mapping) {
    // Handle boolean values (always grant/deny)
    if (typeof mapping === 'boolean') {
        return mapping;
    }
    // Handle undefined (not configured)
    if (!mapping) {
        return false;
    }
    // Handle array of purpose IDs
    if (Array.isArray(mapping)) {
        // Check if Zaraz is available
        if (typeof window === 'undefined' || !window.zaraz?.consent) {
            return false;
        }
        // All specified purposes must be granted
        return mapping.every((purposeId) => {
            const hasConsent = window.zaraz?.consent?.get(purposeId);
            return hasConsent === true;
        });
    }
    return false;
}
/**
 * Checks if Zaraz consent API is ready and available
 * @returns true if Zaraz consent API is ready
 */
export function isZarazConsentReady() {
    if (typeof window === 'undefined') {
        return false;
    }
    return Boolean(window.zaraz?.consent?.APIReady ||
        (window.zaraz?.consent && typeof window.zaraz.consent.get === 'function'));
}
/**
 * Gets the current consent state for all mapped purposes
 * @param purposeMapping The purpose mapping configuration
 * @returns Object with consent state for each purpose
 */
export function getZarazConsentState(purposeMapping) {
    return {
        functional: checkZarazConsent(purposeMapping.functional),
        analytics: checkZarazConsent(purposeMapping.analytics),
        marketing: checkZarazConsent(purposeMapping.marketing),
        preferences: checkZarazConsent(purposeMapping.preferences),
    };
}
// Backward compatibility exports
export { sentryZarazConsentIntegration as default, sentryZarazConsentIntegration as sentryConsentIntegration, };
//# sourceMappingURL=index.js.map